import { ErrorCode } from '@afterpaytouch/portal-api'
import { TerminalErrorCode, isTerminalErrorCode } from '../../model/checkout'
import { UNMAPPED_SIGNALS, toKebabCase } from './signals'
import { JourneyOutcome } from './types'

export type JourneySignalType = 'friction' | 'frustration'

export interface TerminalErrorJourneyMapping {
  outcome: JourneyOutcome.SUCCEEDED | JourneyOutcome.FAILED
  signalType: JourneySignalType
}

const systemFailure: TerminalErrorJourneyMapping = { outcome: JourneyOutcome.FAILED, signalType: 'frustration' }
const businessDecline: TerminalErrorJourneyMapping = { outcome: JourneyOutcome.SUCCEEDED, signalType: 'friction' }

export const TERMINAL_ERROR_JOURNEY_MAPPING: Record<TerminalErrorCode, TerminalErrorJourneyMapping> = {
  [TerminalErrorCode.Default]: systemFailure,
  [TerminalErrorCode.UnableToUpdateCheckout]: systemFailure,
  [TerminalErrorCode.LimitInternalError]: systemFailure,
  [TerminalErrorCode.ErrorPCL]: systemFailure,
  [TerminalErrorCode.IdentityVerificationTimeout]: systemFailure,

  [TerminalErrorCode.AccountClosed]: businessDecline,
  [TerminalErrorCode.AccountDisabled]: businessDecline,
  [TerminalErrorCode.AccountFrozen]: businessDecline,
  [TerminalErrorCode.AccountPendingDeletion]: businessDecline,
  [TerminalErrorCode.ChargebackPending]: businessDecline,
  [TerminalErrorCode.OverduePayments]: businessDecline,
  [TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible]: businessDecline,
  [TerminalErrorCode.ExcessiveLateFeeAmount]: businessDecline,
  [TerminalErrorCode.ExcessiveLateFeeNumber]: businessDecline,
  [TerminalErrorCode.KYCRefreshPending]: businessDecline,
  [TerminalErrorCode.ShadowAccountIneligible]: businessDecline,

  [TerminalErrorCode.RiskDeclined]: businessDecline,
  [TerminalErrorCode.RiskDeclinedPCL]: businessDecline,
  [TerminalErrorCode.AuthenticationDeclined]: businessDecline,
  [TerminalErrorCode.PasswordResetDeclined]: businessDecline,
  [TerminalErrorCode.PasswordResetNotAllowed]: businessDecline,
  [TerminalErrorCode.IdentityDeclined]: businessDecline,
  [TerminalErrorCode.IdentityVerificationFailedManual]: businessDecline,
  [TerminalErrorCode.IdentityVerificationDeclinedPCL]: businessDecline,
  [TerminalErrorCode.PaywatchCheckFailed]: businessDecline,
  [TerminalErrorCode.ExtendedFraudAlert]: businessDecline,
  [TerminalErrorCode.CreditFreezeDeclinedPCL]: businessDecline,
  [TerminalErrorCode.ComplianceDeclineBureauNoHit]: businessDecline,
  [TerminalErrorCode.ComplianceDeclineBureau]: businessDecline,
  [TerminalErrorCode.CreditCheckFailure]: businessDecline,
  [TerminalErrorCode.UnsuitabilityDecline]: businessDecline,
  [TerminalErrorCode.AffordabilityDecline]: businessDecline,
  [TerminalErrorCode.HighRiskNationality]: businessDecline,
  [TerminalErrorCode.DuplicateIdentity]: businessDecline,
  [TerminalErrorCode.BlockSignUpFromFRITES]: businessDecline,

  [TerminalErrorCode.AmountTooHigh]: businessDecline,
  [TerminalErrorCode.PayMonthlyOrderAmountTooHigh]: businessDecline,
  [TerminalErrorCode.AmountTooHighDeclinePCL]: businessDecline,
  [TerminalErrorCode.OrderAmountTooHigh]: businessDecline,
  [TerminalErrorCode.OrderAmountExceedsSpendCap]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReached]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReachedDetails]: businessDecline,
  [TerminalErrorCode.MaxActiveOrdersReachedDeclinePCL]: businessDecline,
  [TerminalErrorCode.MerchantOrderAmountLimitExceeded]: businessDecline,
  [TerminalErrorCode.MerchantLimitZero]: businessDecline,
  [TerminalErrorCode.MinimumNotMet]: businessDecline,
  [TerminalErrorCode.InsufficientFunds]: businessDecline,
  [TerminalErrorCode.InsufficientFundsDecline]: businessDecline,

  [TerminalErrorCode.InvalidPreorder]: businessDecline,
  [TerminalErrorCode.UnsupportedOrder]: businessDecline,
  [TerminalErrorCode.UnsupportedMerchantTradingCountry]: businessDecline,
  [TerminalErrorCode.InvalidAmount]: businessDecline,
  [TerminalErrorCode.InvalidAddress]: businessDecline,
  [TerminalErrorCode.InvalidPickupAddress]: businessDecline,
  [TerminalErrorCode.IneligibleState]: businessDecline,
  [TerminalErrorCode.StateRestrictionsPCL]: businessDecline,
  [TerminalErrorCode.GiftCardIneligible]: businessDecline,
  [TerminalErrorCode.DuplicatePaymentCardError]: businessDecline,

  [TerminalErrorCode.MaxAttempts]: businessDecline,
  [TerminalErrorCode.MaxAttemptsForType]: businessDecline,
  [TerminalErrorCode.MaxDobVerificationAttempts]: businessDecline,
  [TerminalErrorCode.BiometricCheckMaxRetriesReached]: businessDecline,
  [TerminalErrorCode.IdentityVerificationMaxAttemptsAFF]: businessDecline,
  [TerminalErrorCode.ExceededMaxAttemptsEnteringDetails]: businessDecline,
}

export interface TerminalErrorJourneyOutcome extends TerminalErrorJourneyMapping {
  signal: string
}

const SYSTEM_FAULT_ERROR_CODES: string[] = [ErrorCode.IdentityCheckGatewayError]

const ERROR_CODE_RECLASSIFICATIONS: Partial<Record<string, TerminalErrorCode>> = {
  [ErrorCode.RiskDeclineUnclassified]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.DuplicateIdentity]: TerminalErrorCode.DuplicateIdentity,
}

export const mapTerminalErrorToJourney = (error: unknown, errorCode?: unknown): TerminalErrorJourneyOutcome => {
  if (typeof errorCode === 'string' && SYSTEM_FAULT_ERROR_CODES.includes(errorCode)) {
    return { ...systemFailure, signal: toKebabCase(errorCode) }
  }
  const reclassified = typeof errorCode === 'string' ? ERROR_CODE_RECLASSIFICATIONS[errorCode] : undefined
  if (reclassified !== undefined) {
    return { ...TERMINAL_ERROR_JOURNEY_MAPPING[reclassified], signal: toKebabCase(reclassified) }
  }
  if (isTerminalErrorCode(error)) {
    return { ...TERMINAL_ERROR_JOURNEY_MAPPING[error], signal: toKebabCase(error) }
  }
  return {
    ...systemFailure,
    signal: typeof error === 'string' && error.length > 0 ? UNMAPPED_SIGNALS.TERMINAL_ERROR : toKebabCase(TerminalErrorCode.Default),
  }
}
